/* eslint-disable complexity */
import { graphql } from "react-relay";
import { parseRoute } from "src/wac-routes";

import { pricingPageRelayQuery$data } from "@/__generated_contentful__/pricingPageRelayQuery.graphql";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants";

import { richTextToHTML } from "../../clients/uniform-client/enhancers/utils";
import { PricingContentResponse } from "../../utils/pricing-queries/get-pricing-content-gql";

/**
 * Contentful Pricing query (used for all pricing pages)
 */
export const PricingPageRelayQuery = graphql`
    query pricingPageRelayQuery(
        $limit: Int = 1
        $productNameId: String!
        $locale: String!
        $pricingTitleInternal: String = null
    ) {
        pricingCollection(
            limit: $limit
            where: {
                relatedProduct: { productNameId_contains: $productNameId }
                pricingTitleInternal_contains: $pricingTitleInternal
            }
            locale: $locale
        ) {
            items {
                pricingTitleInternal
                pricingTitleExternal
                pageDescription
                headline {
                    json
                }
                comparePlansCollection(limit: 10) {
                    items {
                        planTitleExternal
                        planOneLiner
                        microCtaCollection(limit: 15) {
                            items {
                                ... on CallToActionMicrocopy {
                                    ctaMicrocopyTitleExternal
                                    ctaButtonText
                                    ctaUrl
                                }
                            }
                        }
                        highlightedFeaturesTitle
                        highlightedFeaturesContainerCollection(limit: 15) {
                            items {
                                highlightedFeatureDetails {
                                    json
                                }
                                highlightedFeatureTitleExternal
                                callOut
                            }
                        }
                    }
                }
                compareFeaturesTitle
                compareFeaturesCollection(limit: 10) {
                    items {
                        featureGroupTitleExternal
                        featuresCollection(limit: 20) {
                            items {
                                callOut
                                featureNameExternal
                                description {
                                    json
                                }
                                relevantPlanCollection {
                                    items {
                                        planTitleExternal
                                    }
                                }
                                relevantUrl
                                featureAdditionalInformationCollection(
                                    limit: 15
                                ) {
                                    items {
                                        featureAdditionalInformation
                                        relevantPlanCollection(limit: 15) {
                                            items {
                                                planTitleExternal
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                getMoreDetailsTitle
                additionalDetailsCollection(limit: 15) {
                    items {
                        planAdditionalDetailsTitleExternal
                        planAdditionalDetails {
                            json
                        }
                    }
                }
                relatedProductCollection(limit: 1) {
                    items {
                        productName
                        productNameId
                    }
                }
                callToActionContainerCollection(limit: 10) {
                    items {
                        ... on CallToAction {
                            ctaTitleExternal
                            valueProposition
                            ctaText
                            ctaUrl
                            ctaAsset {
                                title
                                description
                                url
                            }
                        }
                        ... on CallToActionMicrocopy {
                            ctaMicrocopyTitleExternal
                            ctaButtonText
                            ctaUrl
                        }
                    }
                }
                datacenterPlansCollection(limit: 10) {
                    items {
                        planTitleExternal
                        planOneLiner
                        microCtaCollection(limit: 15) {
                            items {
                                ... on CallToActionMicrocopy {
                                    ctaMicrocopyTitleExternal
                                    ctaButtonText
                                    ctaUrl
                                }
                            }
                        }
                        highlightedFeaturesTitle
                        highlightedFeaturesContainerCollection(limit: 15) {
                            items {
                                highlightedFeatureDetails {
                                    json
                                }
                                highlightedFeatureTitleExternal
                                callOut
                            }
                        }
                    }
                }
            }
        }
    }
`;

/**
 * Returns variables for Contentful Anchor Pages query
 */
export const getPricingPageRelayQueryVars = (
    productNameId: string,
    location: Location,
    pricingTitleInternal: string | null = null
) => {
    const { locale } = parseRoute(location);
    return {
        productNameId: productNameId,
        locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US",
        pricingTitleInternal: pricingTitleInternal
    };
};

/**
 * Transforms Contentful Response to shape matching CPAPI response/props
 */
export const shapePricingContentfulResponse = (
    data: pricingPageRelayQuery$data
): PricingContentResponse => {
    // Extract pricingCollection from data if it's nested
    const pricingCollection = data.pricingCollection;

    if (!pricingCollection || !pricingCollection.items) {
        return { pricings: { edges: [] } };
    }

    return {
        pricings: {
            // @ts-ignore
            edges: pricingCollection.items.filter(Boolean).map((item) => ({
                __typename: "ContentPlatformPricingResultEdge",
                node: {
                    __typename: "ContentPlatformPricing",
                    pricingTitleInternal: item?.pricingTitleInternal ?? "",
                    pricingTitleExternal: item?.pricingTitleExternal ?? null,
                    pageDescription: item?.pageDescription ?? null,
                    headline: item?.headline?.json
                        ? richTextToHTML(item.headline.json)
                        : null,
                    comparePlans:
                        item?.comparePlansCollection?.items
                            ?.filter(Boolean)
                            .map((plan) => ({
                                __typename: "ContentPlatformPlan",
                                planTitleExternal:
                                    plan?.planTitleExternal ?? null,
                                planOneLiner: plan?.planOneLiner ?? null,
                                highlightedFeaturesTitle:
                                    plan?.highlightedFeaturesTitle ?? null,
                                microCta:
                                    plan?.microCtaCollection?.items
                                        ?.filter(Boolean)
                                        .map((cta) => ({
                                            __typename:
                                                "ContentPlatformCallToActionMicrocopy",
                                            ctaMicrocopyTitle:
                                                cta?.ctaMicrocopyTitleExternal ??
                                                null,
                                            ctaButtonText:
                                                cta?.ctaButtonText ?? null,
                                            ctaUrl: cta?.ctaUrl ?? null
                                        })) || [],
                                highlightedFeaturesContainer:
                                    plan?.highlightedFeaturesContainerCollection?.items
                                        ?.filter(Boolean)
                                        .map((feature) => ({
                                            __typename:
                                                "ContentPlatformHighlightedFeature",
                                            highlightedFeatureDetails: feature
                                                ?.highlightedFeatureDetails
                                                ?.json
                                                ? richTextToHTML(
                                                      feature
                                                          .highlightedFeatureDetails
                                                          .json
                                                  )
                                                : null,
                                            highlightedFeatureTitleExternal:
                                                feature?.highlightedFeatureTitleExternal ??
                                                null,
                                            callOut: feature?.callOut ?? null
                                        })) || []
                            })) || [],
                    compareFeaturesTitle: item?.compareFeaturesTitle ?? null,
                    compareFeatures:
                        item?.compareFeaturesCollection?.items
                            ?.filter(Boolean)
                            .map((featureGroup) => ({
                                __typename: "ContentPlatformFeatureGroup",
                                featureGroupTitleExternal:
                                    featureGroup?.featureGroupTitleExternal ??
                                    null,
                                features:
                                    featureGroup?.featuresCollection?.items
                                        ?.filter(Boolean)
                                        .map((feature) => ({
                                            __typename:
                                                "ContentPlatformFeature",
                                            callOut: feature?.callOut ?? null,
                                            featureNameExternal:
                                                feature?.featureNameExternal ??
                                                null,
                                            description: feature?.description
                                                ?.json
                                                ? richTextToHTML(
                                                      feature.description.json
                                                  )
                                                : null,
                                            relevantPlan:
                                                feature?.relevantPlanCollection?.items
                                                    ?.filter(Boolean)
                                                    .map((plan) => ({
                                                        __typename:
                                                            "ContentPlatformPlan",
                                                        planTitleExternal:
                                                            plan?.planTitleExternal ??
                                                            null
                                                    })) || [],
                                            relevantUrl:
                                                feature?.relevantUrl ?? null,
                                            featureAdditionalInformation:
                                                feature?.featureAdditionalInformationCollection?.items
                                                    ?.filter(Boolean)
                                                    .map((info) => ({
                                                        __typename:
                                                            "ContentPlatformFeatureAdditionalInformation",
                                                        featureAdditionalInformation:
                                                            info?.featureAdditionalInformation ??
                                                            null,
                                                        relevantPlan:
                                                            info?.relevantPlanCollection?.items
                                                                ?.filter(
                                                                    Boolean
                                                                )
                                                                .map(
                                                                    (plan) => ({
                                                                        __typename:
                                                                            "ContentPlatformPlan",
                                                                        planTitleExternal:
                                                                            plan?.planTitleExternal ??
                                                                            null
                                                                    })
                                                                ) || []
                                                    })) || []
                                        })) || []
                            })) || [],
                    getMoreDetailsTitle: item?.getMoreDetailsTitle ?? null,
                    additionalDetails:
                        item?.additionalDetailsCollection?.items
                            ?.filter(Boolean)
                            .map((detail) => ({
                                __typename: "ContentPlatformPlanDetails",
                                planAdditionalDetailsTitleExternal:
                                    detail?.planAdditionalDetailsTitleExternal ??
                                    null,
                                planAdditionalDetails: detail
                                    ?.planAdditionalDetails?.json
                                    ? richTextToHTML(
                                          detail.planAdditionalDetails.json
                                      )
                                    : null
                            })) || [],
                    relatedProduct:
                        item?.relatedProductCollection?.items
                            ?.filter(Boolean)
                            .map((product) => ({
                                __typename: "ContentPlatformPricingProductName",
                                productName: product?.productName ?? "",
                                productNameId: product?.productNameId ?? ""
                            })) || [],
                    callToActionContainer:
                        item?.callToActionContainerCollection?.items
                            ?.filter(Boolean)
                            .map((cta) => {
                                if (
                                    cta &&
                                    ("ctaTitleExternal" in cta ||
                                        "valueProposition" in cta ||
                                        "ctaText" in cta ||
                                        "ctaAsset" in cta)
                                ) {
                                    // ContentPlatformCallToAction
                                    return {
                                        __typename:
                                            "ContentPlatformCallToAction",
                                        title: cta.ctaTitleExternal ?? null,
                                        valueProposition:
                                            cta.valueProposition ?? null,
                                        text: cta.ctaText ?? null,
                                        url: cta.ctaUrl ?? null,
                                        asset: cta.ctaAsset
                                            ? {
                                                  __typename:
                                                      "ContentPlatformTemplateImageAsset",
                                                  title:
                                                      cta.ctaAsset.title ??
                                                      null,
                                                  description:
                                                      cta.ctaAsset
                                                          .description ?? null,
                                                  url: cta.ctaAsset.url ?? ""
                                              }
                                            : null
                                    };
                                } else if (
                                    cta &&
                                    ("ctaMicrocopyTitleExternal" in cta ||
                                        "ctaButtonText" in cta)
                                ) {
                                    // ContentPlatformCallToActionMicrocopy
                                    return {
                                        __typename:
                                            "ContentPlatformCallToActionMicrocopy",
                                        ctaMicrocopyTitle:
                                            cta.ctaMicrocopyTitleExternal ??
                                            null,
                                        ctaButtonText:
                                            cta.ctaButtonText ?? null,
                                        ctaUrl: cta.ctaUrl ?? null
                                    };
                                }
                                // Filter null values at the map level
                                return null;
                            })
                            .filter(Boolean) || [],
                    datacenterPlans:
                        item?.datacenterPlansCollection?.items
                            ?.filter(Boolean)
                            .map((datacenterPlan) => ({
                                __typename: "ContentPlatformPlan",
                                planTitleExternal:
                                    datacenterPlan?.planTitleExternal ?? null,
                                planOneLiner:
                                    datacenterPlan?.planOneLiner ?? null,
                                microCta:
                                    datacenterPlan?.microCtaCollection?.items
                                        ?.filter(Boolean)
                                        .map((cta) => ({
                                            __typename:
                                                "ContentPlatformCallToActionMicrocopy",
                                            ctaMicrocopyTitle:
                                                cta?.ctaMicrocopyTitleExternal ??
                                                null,
                                            ctaButtonText:
                                                cta?.ctaButtonText ?? null,
                                            ctaUrl: cta?.ctaUrl ?? null
                                        })) || [],
                                highlightedFeaturesTitle:
                                    datacenterPlan?.highlightedFeaturesTitle ??
                                    null,
                                highlightedFeaturesContainer:
                                    datacenterPlan?.highlightedFeaturesContainerCollection?.items
                                        ?.filter(Boolean)
                                        .map((feature) => ({
                                            __typename:
                                                "ContentPlatformHighlightedFeature",
                                            highlightedFeatureDetails: feature
                                                ?.highlightedFeatureDetails
                                                ?.json
                                                ? richTextToHTML(
                                                      feature
                                                          .highlightedFeatureDetails
                                                          .json
                                                  )
                                                : null,
                                            highlightedFeatureTitleExternal:
                                                feature?.highlightedFeatureTitleExternal ??
                                                null,
                                            callOut: feature?.callOut ?? null
                                        })) || []
                            })) || []
                }
            }))
        }
    };
};
